import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import adminSamplePromptService, { SamplePrompt, SamplePromptCreateRequest, SamplePromptUpdateRequest, SamplePromptType } from '../../services/api/adminSamplePromptService';
import SamplePromptFormModal from './SamplePromptFormModal';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  useConfirm,
} from '../tasky';
import { useToast } from '../../contexts/ToastContext';

const SamplePromptList: React.FC = () => {
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [prompts, setPrompts] = useState<SamplePrompt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<SamplePrompt | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<SamplePromptType>('banner');

  const fetchPrompts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminSamplePromptService.getAll();
      setPrompts(data);
    } catch (err) {
      console.error('Failed to fetch sample prompts:', err);
      setError('サンプルプロンプトの読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const openModal = (prompt?: SamplePrompt) => {
    setSelectedPrompt(prompt || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleSubmit = async (data: SamplePromptCreateRequest | SamplePromptUpdateRequest) => {
    setIsSaving(true);
    try {
      if (selectedPrompt) {
        await adminSamplePromptService.update(selectedPrompt.id, data as SamplePromptUpdateRequest);
      } else {
        await adminSamplePromptService.create(data as SamplePromptCreateRequest);
      }
      closeModal();
      await fetchPrompts();
    } catch (err) {
      console.error('Failed to save sample prompt:', err);
      showToast('サンプルプロンプトの保存に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await adminSamplePromptService.toggle(id);
      await fetchPrompts();
    } catch (err) {
      console.error('Failed to toggle sample prompt:', err);
      showToast('ステータスの変更に失敗しました', 'error');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!(await confirm({ title: `"${name}" を削除しますか？`, tone: 'destructive', confirmText: '削除する' }))) return;
    try {
      await adminSamplePromptService.delete(id);
      await fetchPrompts();
    } catch (err) {
      console.error('Failed to delete sample prompt:', err);
      showToast('削除に失敗しました', 'error');
    }
  };

  const filteredPrompts = useMemo(
    () => prompts.filter(p => p.type === activeTab),
    [prompts, activeTab]
  );

  if (isLoading && prompts.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <TaskySpinner size="lg" aria-label="Loading" />
      </div>
    );
  }

  if (error) {
    return <TaskyAlert tone="error">{error}</TaskyAlert>;
  }

  return (
    <>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-dark">サンプルプロンプト一覧</h2>
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary/10 text-primary">
            {prompts.length}件
          </span>
        </div>
        <TaskyButton size="sm" onClick={() => openModal()}>
          <Plus />
          新規追加
        </TaskyButton>
      </div>

      <div className="flex items-end border-b border-warm-border mb-4">
        <button
          type="button"
          onClick={() => setActiveTab('banner')}
          className={`pb-3 px-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'banner'
              ? 'text-primary border-primary'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          広告生成（/ads/banner）
          <span
            className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              activeTab === 'banner'
                ? 'bg-primary/10 text-primary'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            {prompts.filter(p => p.type === 'banner').length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('material')}
          className={`pb-3 px-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'material'
              ? 'text-primary border-primary'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          素材生成（/image-generation）
          <span
            className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              activeTab === 'material'
                ? 'bg-primary/10 text-primary'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            {prompts.filter(p => p.type === 'material').length}
          </span>
        </button>
      </div>

      {filteredPrompts.length > 0 ? (
        <TaskyTable>
          <TaskyTableHead>
            <tr>
              <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>ボタン名</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>プロンプトテキスト</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>表示順</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>ステータス</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
            </tr>
          </TaskyTableHead>
          <TaskyTableBody>
            {filteredPrompts.map(prompt => (
              <TaskyTableRow key={prompt.id}>
                <TaskyTableCell className="text-gray-400 font-mono text-xs">{prompt.id}</TaskyTableCell>
                <TaskyTableCell className="font-medium">{prompt.name}</TaskyTableCell>
                <TaskyTableCell className="text-gray-600">
                  {prompt.prompt_text.length > 80
                    ? prompt.prompt_text.substring(0, 80) + '...'
                    : prompt.prompt_text}
                </TaskyTableCell>
                <TaskyTableCell>{prompt.sort_order}</TaskyTableCell>
                <TaskyTableCell>
                  <TaskyBadge variant={prompt.is_active ? 'success' : 'secondary'}>
                    {prompt.is_active ? '有効' : '無効'}
                  </TaskyBadge>
                </TaskyTableCell>
                <TaskyTableCell>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="px-2.5 py-1 text-xs font-semibold bg-white border border-warm-border text-gray-700 rounded-tasky-xs hover:bg-warm-hover transition-colors"
                      onClick={() => openModal(prompt)}
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      className={
                        prompt.is_active
                          ? 'px-2.5 py-1 text-xs font-semibold bg-warning/10 border border-warning/40 text-warning rounded-tasky-xs hover:bg-warning/20 transition-colors'
                          : 'px-2.5 py-1 text-xs font-semibold bg-success/10 border border-success/40 text-success rounded-tasky-xs hover:bg-success/20 transition-colors'
                      }
                      onClick={() => handleToggle(prompt.id)}
                    >
                      {prompt.is_active ? '無効化' : '有効化'}
                    </button>
                    <button
                      type="button"
                      className="px-2.5 py-1 text-xs font-semibold bg-red-50 border border-red-200 text-red-600 rounded-tasky-xs hover:bg-red-100 transition-colors"
                      onClick={() => handleDelete(prompt.id, prompt.name)}
                    >
                      削除
                    </button>
                  </div>
                </TaskyTableCell>
              </TaskyTableRow>
            ))}
          </TaskyTableBody>
        </TaskyTable>
      ) : (
        <TaskyEmptyState title="サンプルプロンプトが登録されていません。" />
      )}

      <SamplePromptFormModal
        isOpen={isModalOpen}
        prompt={selectedPrompt}
        defaultType={activeTab}
        onSubmit={handleSubmit}
        onClose={closeModal}
        isLoading={isSaving}
      />
    </>
  );
};

export default SamplePromptList;
